import { useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { AiOutlinePlus, AiOutlineMinus } from "react-icons/ai";

import useStepper from "../../../components/stepper/hooks/useStepper";
import { DESIGN_CATEGORY } from "../../../constants/designs";
import { DesignState } from "../../../features/designSlice";
import Shape from "../../../models/furniture/Shape";
import Material from "../../../models/furniture/Material";

interface FavoritesPanelProps {
  options: (Shape | Material)[];
  selectShape: (selectedShape: Shape, elementOrder: number) => void;
  selectMaterial: (
    selectedMaterial: Material,
    elementOrder: number,
    materialOrder: number
  ) => void;
}

interface FavoriteOptions {
  [key: string]: (Shape | Material)[];
}

function FavoritesPanel({
  options,
  selectShape,
  selectMaterial,
}: FavoritesPanelProps) {
  const [favoriteOptions, setFavoriteOptions] = useState<FavoriteOptions>({});
  const [showAllOptions, setShowAllOptions] = useState(false);

  const { currentStepData } = useStepper();
  const { shapes: selectedShapes, materials: selectedMaterials } = useSelector(
    (state: { design: DesignState }) => state.design
  );

  const currentElementOrder = currentStepData?.elementOrder as number;
  const currentMaterialOrder = currentStepData?.materialOrder as number;
  const isShape = currentStepData?.category === DESIGN_CATEGORY.SHAPE;

  const favoriteKey = useMemo(() => {
    return isShape
      ? currentElementOrder?.toString()
      : `${currentElementOrder}_${currentMaterialOrder}`;
  }, [isShape, currentElementOrder, currentMaterialOrder]);

  const currentOptionsCodes = useMemo(
    () => options.map((option) => option.code),
    [options]
  );

  const selectedOption = useMemo(() => {
    return isShape
      ? selectedShapes[currentElementOrder.toString()]
      : selectedMaterials[`${currentElementOrder}_${currentMaterialOrder}`];
  }, [
    isShape,
    selectedShapes,
    selectedMaterials,
    currentElementOrder,
    currentMaterialOrder,
  ]);

  const currentElementFavorites = useMemo(() => {
    const favorites = favoriteOptions[favoriteKey] || [];
    return favorites.filter((option) =>
      currentOptionsCodes.includes(option?.code)
    );
  }, [favoriteOptions, favoriteKey, currentOptionsCodes]);

  const isSelectedOptionInFavorites = useMemo(
    () =>
      currentElementFavorites.some(
        (option) => option?.code === selectedOption?.code
      ),
    [currentElementFavorites, selectedOption?.code]
  );

  const isSelectedOptionInCurrentOptions = useMemo(
    () => options.some((option) => option?.code === selectedOption?.code),
    [options, selectedOption?.code]
  );

  const toggleFavorite = useCallback(() => {
    if (
      !currentElementOrder ||
      !selectedOption ||
      !isSelectedOptionInCurrentOptions
    ) {
      return;
    }

    setFavoriteOptions((prev) => {
      const currentFavorites = prev[favoriteKey] || [];

      const updatedFavorites = isSelectedOptionInFavorites
        ? currentFavorites.filter(
            (option) => option.code !== selectedOption.code
          )
        : [...currentFavorites, selectedOption];

      return {
        ...prev,
        [favoriteKey]: updatedFavorites,
      };
    });
  }, [
    currentElementOrder,
    selectedOption,
    isSelectedOptionInCurrentOptions,
    favoriteKey,
    isSelectedOptionInFavorites,
  ]);

  const handleOptionSelect = useCallback(
    (option: Shape | Material) => {
      if (isShape) {
        selectShape(option as Shape, currentElementOrder);
      } else {
        selectMaterial(
          option as Material,
          currentElementOrder,
          currentMaterialOrder
        );
      }
    },
    [
      isShape,
      selectShape,
      selectMaterial,
      currentElementOrder,
      currentMaterialOrder,
    ]
  );

  const visibleFavorites = useMemo(
    () =>
      showAllOptions
        ? currentElementFavorites
        : currentElementFavorites.slice(0, 5),
    [showAllOptions, currentElementFavorites]
  );

  const hasMoreThanFiveFavorites = currentElementFavorites.length > 5;

  return (
    <div className="flex min-h-[72px] items-center space-x-5 px-6">
      {isSelectedOptionInCurrentOptions && (
        <button
          className="flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded bg-gray-300 hover:bg-gray-400 transition-colors"
          onClick={toggleFavorite}
          aria-label={
            isSelectedOptionInFavorites
              ? "Remove from favorites"
              : "Add to favorites"
          }
        >
          {isSelectedOptionInFavorites ? (
            <AiOutlineMinus size={16} className="text-white" />
          ) : (
            <AiOutlinePlus size={16} className="text-white" />
          )}
        </button>
      )}

      <div className="flex flex-nowrap items-center gap-2 overflow-x-auto overflow-y-hidden">
        {visibleFavorites.map((option, index) => {
          const isSelected = option?.code === selectedOption?.code;

          return (
            <button
              key={`${option.code}-${index}`}
              className={`h-[72px] w-[72px] shrink-0 cursor-pointer overflow-hidden rounded-md border transition-all ${
                isSelected
                  ? "border-highlight scale-90"
                  : "border-gray-200 hover:border-gray-300"
              }`}
              onClick={() => handleOptionSelect(option)}
              aria-label={`Select ${option.name}`}
            >
              <img
                src={option?.image}
                alt={option?.name || "Option"}
                className="aspect-square h-full w-full object-cover"
              />
            </button>
          );
        })}

        {hasMoreThanFiveFavorites && (
          <button
            className="flex h-full cursor-pointer items-center justify-center whitespace-nowrap p-1 text-[12px] font-medium text-primary underline hover:no-underline transition-all"
            onClick={() => setShowAllOptions((prev) => !prev)}
            aria-label={
              showAllOptions ? "Show fewer options" : "Show more options"
            }
          >
            {showAllOptions ? "View Less" : "View More"}
          </button>
        )}
      </div>
    </div>
  );
}

export default FavoritesPanel;
